// Protocol React Hooks
// Custom hooks for fetching and managing protocols

import { useState, useEffect, useCallback } from 'react';
import { protocolService } from '../services/protocolService';

/**
 * Hook to fetch all protocols with optional filters
 */
export function useProtocols(filters) {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchProtocols = useCallback(async () => {
    try {
      setLoading(true);
      const data = await protocolService.getAllProtocols(filters);
      setProtocols(data);
      setError(null);
    } catch (err) {
      setError(err);
      console.error('Error fetching protocols:', err);
    } finally {
      setLoading(false);
    }
  }, [filters?.category, filters?.status, filters?.isCritical, filters?.applicableLocation, filters?.departmentId, filters?.searchTerm]);
  
  useEffect(() => {
    fetchProtocols();
  }, [fetchProtocols]);
  
  const refresh = useCallback(() => {
    fetchProtocols();
  }, [fetchProtocols]);
  
  return { protocols, loading, error, refresh };
}

/**
 * Hook to fetch a single protocol by ID
 */
export function useProtocol(protocolId) {
  const [protocol, setProtocol] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchProtocol = useCallback(async () => {
    if (!protocolId) {
      setProtocol(null);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const data = await protocolService.getProtocol(protocolId);
      setProtocol(data);
      setError(null);
    } catch (err) {
      setError(err);
      console.error('Error fetching protocol:', err);
    } finally {
      setLoading(false);
    }
  }, [protocolId]);
  
  useEffect(() => {
    fetchProtocol();
  }, [fetchProtocol]);
  
  const refresh = useCallback(() => {
    fetchProtocol();
  }, [fetchProtocol]);
  
  return { protocol, loading, error, refresh };
}

/**
 * Hook to fetch protocols by category
 */
export function useProtocolsByCategory(category) {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function fetchProtocols() {
      if (!category) {
        setProtocols([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await protocolService.getProtocolsByCategory(category);
        setProtocols(data);
        setError(null);
      } catch (err) {
        setError(err);
        console.error('Error fetching protocols by category:', err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchProtocols();
  }, [category]);
  
  return { protocols, loading, error };
}

/**
 * Hook to fetch active protocols only
 */
export function useActiveProtocols() {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchProtocols = useCallback(async () => {
    try {
      setLoading(true);
      const data = await protocolService.getActiveProtocols();
      setProtocols(data);
      setError(null);
    } catch (err) {
      setError(err);
      console.error('Error fetching active protocols:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchProtocols();
  }, [fetchProtocols]);
  
  const refresh = useCallback(() => {
    fetchProtocols();
  }, [fetchProtocols]);
  
  return { protocols, loading, error, refresh };
}

/**
 * Hook for protocol mutations (create, update, delete)
 */
export function useProtocolMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const createProtocol = async (protocolData, userId) => {
    try {
      setLoading(true);
      setError(null);
      const id = await protocolService.createProtocol(protocolData, userId);
      return id;
    } catch (err) {
      setError(err);
      console.error('Error creating protocol:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const updateProtocol = async (protocolId, updates, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.updateProtocol(protocolId, updates, userId);
    } catch (err) {
      setError(err);
      console.error('Error updating protocol:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const deleteProtocol = async (protocolId, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.deleteProtocol(protocolId, userId);
    } catch (err) {
      setError(err);
      console.error('Error deleting protocol:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const approveProtocol = async (protocolId, userId, approvalNotes = '') => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.approveProtocol(protocolId, userId, approvalNotes);
    } catch (err) {
      setError(err);
      console.error('Error approving protocol:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const submitForReview = async (protocolId, userId, reviewNotes = '') => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.submitForReview(protocolId, userId, reviewNotes);
    } catch (err) {
      setError(err);
      console.error('Error submitting protocol for review:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const rejectProtocol = async (protocolId, userId, rejectionReason = '') => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.rejectProtocol(protocolId, userId, rejectionReason);
    } catch (err) {
      setError(err);
      console.error('Error rejecting protocol:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const duplicateProtocol = async (protocolId, newVersion, userId) => {
    try {
      setLoading(true);
      setError(null);
      const newId = await protocolService.duplicateProtocolAsCopy(protocolId, newVersion, userId);
      return newId;
    } catch (err) {
      setError(err);
      console.error('Error duplicating protocol:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return {
    createProtocol,
    updateProtocol,
    deleteProtocol,
    approveProtocol,
    submitForReview,
    rejectProtocol,
    duplicateProtocol,
    loading,
    error
  };
}

/**
 * Hook for step mutations
 */
export function useStepMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const addStep = async (protocolId, stepData, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.addStep(protocolId, stepData, userId);
    } catch (err) {
      setError(err);
      console.error('Error adding step:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const updateStep = async (protocolId, stepId, updates, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.updateStep(protocolId, stepId, updates, userId);
    } catch (err) {
      setError(err);
      console.error('Error updating step:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const deleteStep = async (protocolId, stepId, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.deleteStep(protocolId, stepId, userId);
    } catch (err) {
      setError(err);
      console.error('Error deleting step:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const reorderSteps = async (protocolId, stepIds, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.reorderSteps(protocolId, stepIds, userId);
    } catch (err) {
      setError(err);
      console.error('Error reordering steps:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return {
    addStep,
    updateStep,
    deleteStep,
    reorderSteps,
    loading,
    error
  };
}

/**
 * Hook for action mutations
 */
export function useActionMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const addAction = async (protocolId, stepId, actionData, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.addAction(protocolId, stepId, actionData, userId);
    } catch (err) {
      setError(err);
      console.error('Error adding action:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const updateAction = async (protocolId, stepId, actionId, updates, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.updateAction(protocolId, stepId, actionId, updates, userId);
    } catch (err) {
      setError(err);
      console.error('Error updating action:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const deleteAction = async (protocolId, stepId, actionId, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.deleteAction(protocolId, stepId, actionId, userId);
    } catch (err) {
      setError(err);
      console.error('Error deleting action:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  const reorderActions = async (protocolId, stepId, actionIds, userId) => {
    try {
      setLoading(true);
      setError(null);
      await protocolService.reorderActions(protocolId, stepId, actionIds, userId);
    } catch (err) {
      setError(err);
      console.error('Error reordering actions:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  return {
    addAction,
    updateAction,
    deleteAction,
    reorderActions,
    loading,
    error
  };
}

/**
 * Hook to fetch protocol completion statistics
 */
export function useProtocolCompletionStats(protocolId, dateRange = null) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await protocolService.getProtocolCompletionStats(protocolId, dateRange);
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err);
      console.error('Error fetching protocol completion stats:', err);
    } finally {
      setLoading(false);
    }
  }, [protocolId, dateRange?.start, dateRange?.end]);
  
  useEffect(() => {
    if (protocolId) {
      fetchStats();
    }
  }, [fetchStats]);
  
  const refresh = useCallback(() => {
    fetchStats();
  }, [fetchStats]);
  
  return { stats, loading, error, refresh };
}

/**
 * Hook to fetch completion statistics for all protocols
 */
export function useAllProtocolsCompletionStats(dateRange = null) {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await protocolService.getAllProtocolsCompletionStats(dateRange);
      setStats(data);
      setError(null);
    } catch (err) {
      setError(err);
      console.error('Error fetching all protocols completion stats:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange?.start, dateRange?.end]);
  
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);
  
  const refresh = useCallback(() => {
    fetchStats();
  }, [fetchStats]);
  
  return { stats, loading, error, refresh };
}

/**
 * Hook to fetch protocol completion trend over time
 */
export function useProtocolCompletionTrend(protocolId, days = 30) {
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchTrend = useCallback(async () => {
    try {
      setLoading(true);
      const data = await protocolService.getProtocolCompletionTrend(protocolId, days);
      setTrend(data);
      setError(null);
    } catch (err) {
      setError(err);
      console.error('Error fetching protocol completion trend:', err);
    } finally {
      setLoading(false);
    }
  }, [protocolId, days]);
  
  useEffect(() => {
    if (protocolId) {
      fetchTrend();
    }
  }, [fetchTrend]);
  
  const refresh = useCallback(() => {
    fetchTrend();
  }, [fetchTrend]);
  
  return { trend, loading, error, refresh };
}

